import React, { useState } from 'react';
import {
    FaStar,
    FaSmile,
    FaPaperPlane,
    FaCheckCircle,
    FaArrowLeft,
    FaSpinner,
    FaExclamationTriangle
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { surveyService } from '../../services/surveyService';

const SurveyPage = () => {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        layanan: '',
        kecepatan: 0,
        keramahan: 0,
        kemudahan: 0,
        fasilitas: 0,
        overall: 0,
        saran: '',
        nama: '',
        email: '',
        telepon: ''
    });
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [notification, setNotification] = useState(null);

    const layananOptions = [
        'Retribusi IPT',
        'Retribusi IPR',
        'Perizinan Aset',
        'Sewa Gedung',
        'Sewa Kendaraan',
        'Surat Keterangan',
        'Konsultasi',
        'Lainnya'
    ];

    const handleRating = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    // ✅ Validasi per step
    const validateStep = (stepNumber) => {
        const newErrors = {};

        if (stepNumber === 1) {
            if (!formData.layanan) newErrors.layanan = 'Silakan pilih layanan';
        }

        if (stepNumber === 2) {
            if (formData.kecepatan === 0) newErrors.kecepatan = 'Silakan berikan penilaian kecepatan';
            if (formData.keramahan === 0) newErrors.keramahan = 'Silakan berikan penilaian keramahan';
            if (formData.kemudahan === 0) newErrors.kemudahan = 'Silakan berikan penilaian kemudahan';
            if (formData.fasilitas === 0) newErrors.fasilitas = 'Silakan berikan penilaian fasilitas';
        }

        if (stepNumber === 3) {
            if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
                newErrors.email = 'Format email tidak valid';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const nextStep = () => {
        if (validateStep(step)) {
            setStep(prev => prev + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const prevStep = () => {
        setStep(prev => prev - 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // ✅ Submit ke API
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateStep(3)) return;

        setSubmitting(true);
        setNotification(null);

        const result = await surveyService.submitSurvey(formData);

        if (result.success) {
            setSubmitted(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            setNotification({
                type: 'error',
                message: result.message || 'Gagal mengirim survey. Silakan coba lagi.'
            });
            if (result.errors) {
                setErrors(result.errors);
            }
        }

        setSubmitting(false);
    };

    // ============ SUCCESS VIEW ============
    if (submitted) {
        return (
            <div className="container mx-auto px-4 py-12">
                <div className="max-w-2xl mx-auto text-center">
                    <div className="card-glass">
                        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                            <FaCheckCircle className="text-6xl text-green-500" />
                        </div>
                        <h2 className="text-3xl font-bold text-[#0F4C3A] mb-4">
                            Terima Kasih Atas Partisipasi Anda!
                        </h2>
                        <p className="text-gray-600 mb-6">
                            Survey Anda sangat berharga untuk meningkatkan kualitas pelayanan BPKAD Kota Magelang.
                        </p>
                        <div className="flex justify-center space-x-1 mb-6">
                            {[1, 2, 3, 4, 5].map(star => (
                                <FaStar key={star} className="text-yellow-400 text-3xl" />
                            ))}
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                            <p className="text-sm text-green-800">
                                ✅ Survey Anda telah tersimpan di sistem kami
                            </p>
                        </div>
                        <Link to="/" className="btn-primary inline-flex items-center">
                            <FaArrowLeft className="mr-2" /> Kembali ke Beranda
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // ============ FORM VIEW ============
    return (
        <div className="container mx-auto px-4 py-12">
            <div className="max-w-3xl mx-auto">

                {/* Header */}
                <div className="text-center mb-8">
                    <FaSmile className="text-5xl text-[#2E7D32] mx-auto mb-4" />
                    <h1 className="text-3xl font-bold text-[#0F4C3A] mb-2">
                        Survey Kepuasan Pelanggan
                    </h1>
                    <p className="text-gray-600">
                        Bantu kami meningkatkan kualitas pelayanan dengan mengisi survey berikut
                    </p>
                </div>

                {/* Progress */}
                <div className="flex items-center justify-center mb-8">
                    {[1, 2, 3].map((s) => (
                        <React.Fragment key={s}>
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${step >= s ? 'bg-[#2E7D32] text-white' : 'bg-gray-300 text-gray-600'
                                }`}>
                                {step > s ? '✓' : s}
                            </div>
                            {s < 3 && (
                                <div className={`w-16 h-1 transition-all ${step > s ? 'bg-[#2E7D32]' : 'bg-gray-300'
                                    }`} />
                            )}
                        </React.Fragment>
                    ))}
                </div>

                {/* Notification */}
                {notification && (
                    <div className={`mb-4 p-4 rounded-lg flex items-center ${notification.type === 'error'
                            ? 'bg-red-50 border border-red-200 text-red-700'
                            : 'bg-green-50 border border-green-200 text-green-700'
                        }`}>
                        <FaExclamationTriangle className="mr-2" />
                        {notification.message}
                    </div>
                )}

                <div className="card-glass">
                    <form onSubmit={handleSubmit}>

                        {/* ============ STEP 1 ============ */}
                        {step === 1 && (
                            <div className="space-y-6">
                                <h3 className="text-xl font-bold text-[#0F4C3A]">
                                    Layanan yang Digunakan
                                </h3>

                                {errors.layanan && (
                                    <p className="text-red-500 text-sm">{errors.layanan}</p>
                                )}

                                <div className="grid grid-cols-2 gap-3">
                                    {layananOptions.map((layanan) => (
                                        <button
                                            key={layanan}
                                            type="button"
                                            onClick={() => {
                                                setFormData(prev => ({ ...prev, layanan }));
                                                setErrors(prev => ({ ...prev, layanan: '' }));
                                            }}
                                            className={`p-4 rounded-lg border-2 transition-all text-left ${formData.layanan === layanan
                                                    ? 'border-[#2E7D32] bg-[#2E7D32]/5 text-[#0F4C3A] font-semibold'
                                                    : 'border-gray-200 hover:border-gray-300'
                                                }`}
                                        >
                                            {layanan}
                                        </button>
                                    ))}
                                </div>

                                <button
                                    type="button"
                                    onClick={nextStep}
                                    className="btn-primary w-full"
                                >
                                    Selanjutnya
                                </button>
                            </div>
                        )}

                        {/* ============ STEP 2 ============ */}
                        {step === 2 && (
                            <div className="space-y-6">
                                <h3 className="text-xl font-bold text-[#0F4C3A]">
                                    Penilaian Layanan
                                </h3>

                                {[
                                    { field: 'kecepatan', label: 'Kecepatan Pelayanan' },
                                    { field: 'keramahan', label: 'Keramahan Petugas' },
                                    { field: 'kemudahan', label: 'Kemudahan Proses' },
                                    { field: 'fasilitas', label: 'Kenyamanan Fasilitas' }
                                ].map((item) => (
                                    <div key={item.field}>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {item.label} <span className="text-red-500">*</span>
                                        </label>
                                        <div className="flex space-x-2">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <button
                                                    key={star}
                                                    type="button"
                                                    onClick={() => handleRating(item.field, star)}
                                                    className="text-3xl transition-all hover:scale-125 focus:outline-none"
                                                >
                                                    <FaStar className={
                                                        formData[item.field] >= star
                                                            ? 'text-yellow-400'
                                                            : 'text-gray-300'
                                                    } />
                                                </button>
                                            ))}
                                            {formData[item.field] > 0 && (
                                                <span className="ml-2 text-sm text-gray-500 self-center">
                                                    {formData[item.field]}/5
                                                </span>
                                            )}
                                        </div>
                                        {errors[item.field] && (
                                            <p className="text-red-500 text-xs mt-1">{errors[item.field]}</p>
                                        )}
                                    </div>
                                ))}

                                <div className="flex space-x-3">
                                    <button
                                        type="button"
                                        onClick={prevStep}
                                        className="flex-1 border border-gray-300 rounded-lg py-2 hover:bg-gray-50 transition-colors"
                                    >
                                        Sebelumnya
                                    </button>
                                    <button
                                        type="button"
                                        onClick={nextStep}
                                        className="flex-1 btn-primary"
                                    >
                                        Selanjutnya
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ============ STEP 3 ============ */}
                        {step === 3 && (
                            <div className="space-y-6">
                                <h3 className="text-xl font-bold text-[#0F4C3A]">
                                    Data & Saran
                                </h3>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Saran & Masukan
                                    </label>
                                    <textarea
                                        value={formData.saran}
                                        onChange={(e) => setFormData(prev => ({ ...prev, saran: e.target.value }))}
                                        rows="4"
                                        maxLength="2000"
                                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                                        placeholder="Tulis saran Anda untuk perbaikan layanan"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">
                                        {formData.saran.length}/2000 karakter
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Nama
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.nama}
                                            onChange={(e) => setFormData(prev => ({ ...prev, nama: e.target.value }))}
                                            maxLength="100"
                                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                                            placeholder="Nama Anda (opsional)"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Telepon
                                        </label>
                                        <input
                                            type="tel"
                                            value={formData.telepon}
                                            onChange={(e) => setFormData(prev => ({ ...prev, telepon: e.target.value }))}
                                            maxLength="20"
                                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                                            placeholder="Nomor telepon (opsional)"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                            maxLength="100"
                                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent ${errors.email ? 'border-red-500 bg-red-50' : ''
                                                }`}
                                            placeholder="Email Anda (opsional)"
                                        />
                                        {errors.email && (
                                            <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="flex space-x-3">
                                    <button
                                        type="button"
                                        onClick={prevStep}
                                        className="flex-1 border border-gray-300 rounded-lg py-2 hover:bg-gray-50 transition-colors"
                                    >
                                        Sebelumnya
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className={`flex-1 btn-primary flex items-center justify-center ${submitting ? 'opacity-50 cursor-not-allowed' : ''
                                            }`}
                                    >
                                        {submitting ? (
                                            <>
                                                <FaSpinner className="animate-spin mr-2" />
                                                Mengirim...
                                            </>
                                        ) : (
                                            <>
                                                <FaPaperPlane className="mr-2" />
                                                Kirim Survey
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SurveyPage;